/**
 * Componente: Modal de Gestión de Errores — Onboarding AviFácil
 * Arquitectura modular y ergonómica para resolver incidencias de registro
 * (Duplicidad, Sesión Expirada, Red/Timeout, Aprovisionamiento, Rate Limit).
 */

'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './OnboardingErrorModal.module.css';

export type TipoErrorOnboarding =
  | 'DUPLICADO'
  | 'EXPIRADO'
  | 'CONEXION'
  | 'TRANSACCION'
  | 'RATE_LIMIT'
  | 'GENERICO';

export interface OnboardingErrorModalProps {
  isOpen: boolean;
  tipo: TipoErrorOnboarding;
  titulo?: string;
  mensaje?: string;
  detalleContextual?: string;
  onAccionPrincipal?: () => void;
  onAccionSecundaria?: () => void;
  onClose: () => void;
}

interface ErrorConfig {
  icon: string;
  iconClass: string;
  tituloDefault: string;
  mensajeDefault: string;
  textoBtnPrincipal: string;
  btnPrincipalClass: string;
  textoBtnSecundario?: string;
}

const ERROR_CONFIGS: Record<TipoErrorOnboarding, ErrorConfig> = {
  DUPLICADO: {
    icon: 'person_alert',
    iconClass: styles.iconYellow,
    tituloDefault: 'Ya tienes una cuenta registrada',
    mensajeDefault:
      'El correo electrónico o número de WhatsApp suministrado ya está asociado a una cuenta activa en AviFácil.',
    textoBtnPrincipal: 'Iniciar Sesión',
    btnPrincipalClass: styles.btnPurple,
    textoBtnSecundario: 'Corregir mis datos',
  },
  EXPIRADO: {
    icon: 'history_toggle_off',
    iconClass: styles.iconPurple,
    tituloDefault: 'Tu sesión de registro ha expirado',
    mensajeDefault:
      'Por motivos de seguridad y resguardo de tus datos, el borrador temporal ha vencido por inactividad.',
    textoBtnPrincipal: 'Reiniciar Registro',
    btnPrincipalClass: styles.btnPurple,
    textoBtnSecundario: 'Ayuda por WhatsApp',
  },
  CONEXION: {
    icon: 'wifi_off',
    iconClass: styles.iconCoral,
    tituloDefault: 'Sin conexión con el servidor',
    mensajeDefault:
      'No pudimos sincronizar tus datos debido a una interrupción en tu conexión de internet. Tus datos no se han perdido.',
    textoBtnPrincipal: 'Reintentar ahora',
    btnPrincipalClass: styles.btnGreen,
    textoBtnSecundario: 'Cerrar',
  },
  TRANSACCION: {
    icon: 'warning',
    iconClass: styles.iconCoral,
    tituloDefault: 'No pudimos configurar tu granja',
    mensajeDefault:
      'Ocurrió una interrupción temporal al aprovisionar tu cuenta y granja en el sistema. Por favor intenta de nuevo.',
    textoBtnPrincipal: 'Reintentar',
    btnPrincipalClass: styles.btnCoral,
    textoBtnSecundario: 'Hablar con Elie por WhatsApp',
  },
  RATE_LIMIT: {
    icon: 'hourglass_empty',
    iconClass: styles.iconYellow,
    tituloDefault: 'Por favor, espera un momento',
    mensajeDefault:
      'Hemos recibido múltiples solicitudes en un lapso corto de tiempo. Por motivos de seguridad, aguarda unos segundos antes de reintentar.',
    textoBtnPrincipal: 'Entendido',
    btnPrincipalClass: styles.btnPurple,
  },
  GENERICO: {
    icon: 'error',
    iconClass: styles.iconCoral,
    tituloDefault: 'Ha ocurrido un inconveniente',
    mensajeDefault:
      'Algo no salió como esperábamos al procesar tu solicitud. Por favor intenta nuevamente.',
    textoBtnPrincipal: 'Reintentar',
    btnPrincipalClass: styles.btnPurple,
    textoBtnSecundario: 'Cerrar',
  },
};

export const OnboardingErrorModal: React.FC<OnboardingErrorModalProps> = ({
  isOpen,
  tipo,
  titulo,
  mensaje,
  detalleContextual,
  onAccionPrincipal,
  onAccionSecundaria,
  onClose,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Cerrar al pulsar Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const config = ERROR_CONFIGS[tipo] || ERROR_CONFIGS.GENERICO;

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-error-modal-title"
    >
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className={styles.closeButton}
          aria-label="Cerrar modal de error"
        >
          <span className="material-symbols-outlined text-[20px]">close</span>
        </button>

        {/* Icono de Error (Sin fondo, solo icono con color semántico) */}
        <div className={`${styles.iconWrapper} ${config.iconClass}`}>
          <span className="material-symbols-outlined text-[56px]">
            {config.icon}
          </span>
        </div>

        {/* Título */}
        <h2 id="onboarding-error-modal-title" className={styles.title}>
          {titulo || config.tituloDefault}
        </h2>

        {/* Mensaje */}
        <p className={styles.message}>
          {mensaje || config.mensajeDefault}
        </p>

        {/* Caja de Contexto / Detalle (si aplica) */}
        {detalleContextual && (
          <div className={styles.contextCard}>
            <span className={`material-symbols-outlined ${styles.contextIcon}`}>
              info
            </span>
            <div className={styles.contextText}>
              <span className={styles.contextStrong}>Detalle: </span>
              {detalleContextual}
            </div>
          </div>
        )}

        {/* Botones de Acción */}
        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={() => {
              if (onAccionPrincipal) {
                onAccionPrincipal();
              } else {
                onClose();
              }
            }}
            className={`${styles.primaryButton} ${config.btnPrincipalClass}`}
          >
            <span>{config.textoBtnPrincipal}</span>
            <span className="material-symbols-outlined text-[19px]">
              arrow_forward
            </span>
          </button>

          {config.textoBtnSecundario && (
            <button
              type="button"
              onClick={() => {
                if (onAccionSecundaria) {
                  onAccionSecundaria();
                } else {
                  onClose();
                }
              }}
              className={styles.secondaryButton}
            >
              <span>{config.textoBtnSecundario}</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
